import React, { useContext } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Cog6ToothIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { UserContext } from "../context/UserContext";
import CommandSearch from "./CommandSearch";

function Logo() {
  return (
    <img
      src="/Predict90Wordmark.png"
      alt="Predict90"
      className="h-7 md:h-8 w-auto"
      onError={(e) => {
        e.target.style.display = "none";
        const fallback = document.createElement("span");
        fallback.textContent = "Predict90";
        fallback.className = "text-xl font-bold text-ink";
        e.target.parentElement.appendChild(fallback);
      }}
    />
  );
}

const barClass =
  "bg-white/95 backdrop-blur sticky top-0 z-50 border-b border-line pt-safe";
const rowClass =
  "max-w-7xl mx-auto px-4 md:px-6 flex justify-between items-center h-14 md:h-16";

export default function Navbar() {
  const { user, setUser, isAdmin } = useContext(UserContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/auth", { replace: true });
  };

  if (!user) {
    return (
      <nav className={barClass}>
        <div className={rowClass}>
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/auth"
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-ink transition-colors"
            >
              Login
            </Link>
            <Link
              to="/auth"
              className="px-4 py-2 bg-brand hover:bg-brand-hover text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Register
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  const canTablePredict =
    Boolean(user?.has_table_prediction_access) || Boolean(isAdmin);

  const navLinks = [
    { label: "Home", to: "/dashboard" },
    { label: "Predict", to: "/predictions" },
    { label: "Fixtures", to: "/fixtures" },
    { label: "League", to: "/leaderboard" },
    ...(canTablePredict ? [{ label: "Table", to: "/table-predictions" }] : []),
    { label: "Profile", to: "/profile" },
  ];
  if (isAdmin) navLinks.push({ label: "Admin", to: "/admin" });

  const linkClass = ({ isActive }) =>
    `relative px-3 py-2 text-sm font-semibold transition-colors after:absolute after:left-3 after:right-3 after:-bottom-[13px] after:h-0.5 after:rounded-full ${
      isActive
        ? "text-brand after:bg-brand"
        : "text-gray-600 hover:text-ink after:bg-transparent"
    }`;

  return (
    <nav className={barClass}>
      <div className={rowClass}>
        <NavLink to="/dashboard" className="flex items-center">
          <Logo />
        </NavLink>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((ln) => (
            <NavLink key={ln.to} to={ln.to} className={linkClass}>
              {ln.label}
            </NavLink>
          ))}

          <span className="w-px h-6 bg-line mx-2" aria-hidden />

          {user?.username && (
            <span
              className="hidden lg:inline max-w-[160px] truncate text-sm font-medium text-muted"
              title={user.username}
            >
              {user.username}
            </span>
          )}

          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("p90-open-search"))}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-line px-2.5 text-sm font-medium text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            <span className="hidden lg:inline">Search</span>
          </button>

          <NavLink
            to="/settings"
            className="p-2 text-gray-600 hover:text-ink transition-colors"
            aria-label="Settings"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </NavLink>

          <button
            onClick={handleLogout}
            className="px-3 py-2 text-sm font-semibold text-gray-600 hover:text-red-600 transition-colors"
          >
            Log out
          </button>
        </div>

        <div className="flex items-center md:hidden">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("p90-open-search"))}
            className="p-2.5 text-gray-600 hover:text-ink"
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="h-6 w-6" />
          </button>
          <NavLink
            to="/settings"
            className="-mr-2 p-2.5 text-gray-600 hover:text-ink transition-colors"
            aria-label="Settings"
          >
            <Cog6ToothIcon className="h-6 w-6" />
          </NavLink>
        </div>
      </div>
      <CommandSearch />
    </nav>
  );
}
